import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { Howl } from "howler";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const howlRef = useRef(null);
  const rafRef = useRef(null);

  const updateProgress = useCallback(() => {
    if (howlRef.current && howlRef.current.playing()) {
      const seek = howlRef.current.seek() || 0;
      setProgress(seek);
      rafRef.current = requestAnimationFrame(updateProgress);
    }
  }, []);

  const playTrack = useCallback(
    (beat) => {
      // If same track, toggle play/pause
      if (currentTrack && currentTrack.id === beat.id) {
        if (isPlaying) {
          howlRef.current?.pause();
          setIsPlaying(false);
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
          return;
        } else {
          howlRef.current?.play();
          setIsPlaying(true);
          rafRef.current = requestAnimationFrame(updateProgress);
          return;
        }
      }

      // Stop current
      if (howlRef.current) {
        howlRef.current.stop();
        howlRef.current.unload();
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      }

      const previewUrl = beat.preview_url.startsWith("http")
        ? beat.preview_url
        : `${BACKEND_URL}${beat.preview_url}`;

      const howl = new Howl({
        src: [previewUrl],
        format: ["wav"],
        html5: true,
        volume: volume,
        onplay: () => {
          setIsPlaying(true);
          setDuration(howl.duration());
          rafRef.current = requestAnimationFrame(updateProgress);
        },
        onpause: () => {
          setIsPlaying(false);
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
        },
        onend: () => {
          setIsPlaying(false);
          setProgress(0);
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
        },
        onload: () => {
          setDuration(howl.duration());
        },
      });

      howlRef.current = howl;
      setCurrentTrack(beat);
      setProgress(0);
      howl.play();

      // Track play count
      fetch(`${BACKEND_URL}/api/beats/${beat.id}/play`, { method: "POST" }).catch(() => {});
    },
    [currentTrack, isPlaying, volume, updateProgress]
  );

  const seek = useCallback((time) => {
    if (howlRef.current) {
      howlRef.current.seek(time);
      setProgress(time);
    }
  }, []);

  const setVolumeLevel = useCallback((v) => {
    setVolume(v);
    if (howlRef.current) {
      howlRef.current.volume(v);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (howlRef.current) {
        howlRef.current.stop();
        howlRef.current.unload();
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <PlayerContext.Provider
      value={{ currentTrack, isPlaying, progress, duration, volume, playTrack, seek, setVolumeLevel }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within PlayerProvider");
  return ctx;
}
